
import Vue, { PropType } from 'vue';
import { ChartData, ChartOptions, ChartTooltipItem } from 'chart.js';
import { Validation } from 'vuelidate/vuelidate';
import { integer, decimal, minValue, required } from 'vuelidate/lib/validators';

import Modal from '@/components/modal/Modal.vue';
import BarChart from '@/components/charts/BarChart.vue';
import UnitInput from '@/components/input/UnitInput.vue';
import { EnergyGraph, EnergyInfo, EnergyParams, ModifyZoneEnergy } from '@/interfaces/energy';
import { Room, Zone } from '@/interfaces/lighting';

interface EnergyForm {
	previousEnergyConsumption: number;
	expectedWorkingHours: number;
}

export default Vue.extend({
	components: {
		Modal,
		BarChart,
		UnitInput,
	},

	data(): {
		editMode: boolean;
		energyForm: EnergyForm;
		chartOptions: ChartOptions;
	} {
		return {
			editMode: false,
			energyForm: {
				previousEnergyConsumption: 0,
				expectedWorkingHours: 0,
			},
			chartOptions: {
				responsive: true,
				maintainAspectRatio: false,
				legend: {
					labels: {
						fontColor: '#fff',
					},
				},
				scales: {
					yAxes: [
						{
							scaleLabel: {
								labelString: 'kWh',
								display: true,
								fontColor: 'fff',
							},
							gridLines: {
								color: '#0c1226',
							},
							ticks: {
								fontColor: '#fff',
							},
						},
					],
					xAxes: [
						{
							gridLines: {
								color: '#0c1226',
							},
							ticks: {
								fontColor: '#fff',
							},
						},
					],
				},
				tooltips: {
					callbacks: {
						label(tooltipItem: ChartTooltipItem, data: ChartData) {
							const start = data.datasets?.[tooltipItem.datasetIndex as number]?.label ?? '';

							let value: string;
							if (typeof tooltipItem.yLabel === 'number') {
								value = tooltipItem.yLabel.toFixed(2);
							} else {
								value = tooltipItem.yLabel!;
							}

							if (start) {
								return `${start}: ${value} kWh`;
							} else {
								return `${value} kWh`;
							}
						},
					},
				},
			},
		};
	},

	validations: {
		energyForm: {
			previousEnergyConsumption: {
				required,
				decimal,
				minValue: minValue(0),
			},
			expectedWorkingHours: {
				required,
				integer,
				minValue: minValue(0),
			},
		},
	},

	props: {
		location: { type: Object as PropType<Room | Zone> },
		paramToggle: { type: Boolean, default: false },
		energyGraph: { type: Object as PropType<EnergyGraph> },
		energyParams: { type: Object as PropType<EnergyParams> },
		extraInfo: { type: Object as PropType<EnergyInfo> },
		userPermission: { type: Boolean, default: true },
	},

	computed: {
		chartData(): ChartData {
			if (this.energyGraph) {
				const previousConsumptionSet: number[] = [
					this.energyParams?.previous_energy_consumption ?? 0,
					this.energyParams?.previous_energy_consumption ?? 0,
					this.energyParams?.previous_energy_consumption ?? 0,
				];
				const projectedSet: number[] = [
					this.energyGraph.calculated_energy,
					this.energyGraph.calculated_energy,
					this.energyGraph.calculated_energy,
				];
				const totalEnergy: number[] = [0, this.energyGraph.real_energy, 0];

				return {
					labels: ['', this.$i18n.t('months.current_month').toString(), ''],
					datasets: [
						{
							label: this.$i18n.t('Previous consumption').toString(),
							fill: false,
							borderColor: '#921c53',
							data: previousConsumptionSet,
							type: 'line',
							order: 1,
						},
						{
							label: this.$i18n.t('Projected consumption').toString(),
							fill: false,
							borderColor: '#cf7430',
							data: projectedSet,
							type: 'line',
							order: 2,
						},
						{
							label: this.$i18n.t('Actual consumption').toString(),
							backgroundColor: '#44a26a',
							data: totalEnergy,
							order: 3,
						},
					],
				};
			} else {
				return { labels: [], datasets: [] };
			}
		},
	},

	watch: {
		energyParams: {
			deep: true,
			immediate: true,
			handler(energyParams: EnergyParams): void {
				this.energyForm = this.makeEnergyForm(energyParams);
				this.editMode = false;
			},
		},
	},

	methods: {
		show(): void {
			this.$bvModal.show('modal-energy');
		},

		hide(): void {
			this.$bvModal.hide('modal-energy');
		},

		reset(): void {
			this.energyForm = this.makeEnergyForm(undefined);
			this.$v.energyForm.$reset();
			this.editMode = false;
			this.$emit('reset');
		},

		validateState(validation: Validation): boolean | null {
			const { $dirty, $error } = validation;
			return $dirty ? !$error : null;
		},

		confirm(): void {
			this.$v.energyForm.$touch();
			if (this.$v.energyForm.$anyError) {
				return;
			}

			const energyParams: ModifyZoneEnergy = {
				id: this.location!.id,
				previous_energy_consumption: this.energyForm.previousEnergyConsumption,
				expected_working_hours: this.energyForm.expectedWorkingHours,
			};
			this.$emit('modifyEnergy', energyParams);
		},

		changeEditMode(status: boolean): void {
			if (status) {
				this.$set(this, 'energyForm', this.makeEnergyForm(this.energyParams));
			}

			this.editMode = !this.editMode;
		},

		makeEnergyForm(energyParams: EnergyParams | undefined): EnergyForm {
			return {
				previousEnergyConsumption: energyParams ? energyParams.previous_energy_consumption! : 0,
				expectedWorkingHours: energyParams ? energyParams.expected_working_hours! : 0,
			};
		},
	},
});
